/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser'

const tenant = process.env.REACT_APP_ADB2C_TENANT
const clientId = process.env.REACT_APP_ADB2C_CLIENT_ID
const authorityDomain =
  process.env.REACT_APP_ADB2C_AUTHORITY_DOMAIN || `${tenant}.b2clogin.com`
const signUpSignIn = 'B2C_1A_SIGNUP_SIGNIN_USERMGMT'
const forgotPassword = 'B2C_1_reset_v3'
const editProfile = 'B2C_1_edit_profile_v2'

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: signUpSignIn,
    forgotPassword: forgotPassword,
    editProfile: editProfile,
  },
  authorities: {
    signUpSignIn: {
      authority: `https://${authorityDomain}/${tenant}.onmicrosoft.com/${signUpSignIn}`,
    },
    forgotPassword: {
      authority: `https://${authorityDomain}/${tenant}.onmicrosoft.com/${forgotPassword}`,
    },
    editProfile: {
      authority: `https://${authorityDomain}/${tenant}.onmicrosoft.com/${editProfile}`,
    },
  },
  authorityDomain: authorityDomain,
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: clientId ?? '', // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: '/users', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ): void => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Info:
            //console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
          default:
            return
        }
      },
    },
  },
}

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  userManagement: {
    rolesEndpoint: `${process.env.REACT_APP_FUNC_API_BASE_URL}/roles`,
    scopes: [
      `https://${process.env.REACT_APP_ADB2C_TENANT}.onmicrosoft.com/usermanagement/admin`,
    ],
  },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  // scopes: [...protectedResources.userManagement.scopes],
  scopes: [
    'openid',
    `https://${process.env.REACT_APP_ADB2C_TENANT}.onmicrosoft.com/usermanagement/admin`,
  ],
}

/**
 * IMPORTANT: In case of overage, group list is cached for 1 hr by default, and thus cached groups
 * will miss any changes to a users group membership for this duration. For capturing real-time
 * changes to a user's group membership, consider implementing Microsoft Graph change notifications.
 * For more information, visit: https://learn.microsoft.com/graph/api/resources/webhooks
 */
export const CACHE_TTL_IN_MS = 60 * 60 * 1000 // 1 hour in milliseconds
